import { useEffect } from 'react';

import AppStoreBadges from '@/components/shared/AppStoreBadges';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

type PushStatus = {
  enabled: boolean;
  count: number;
  platforms: string[];
  lastSeenAt: string | null;
};

const statusRequest: RequestSchema<PushStatus> = {
  name: 'pushStatus',
  input: '/api/v1/deviceToken',
  method: 'GET',
};

/**
 * Shows whether this account is receiving native push notifications (i.e. has a
 * live registered device). Read-only: the OS owns the permission toggle, and a
 * device registers itself on app launch — so here we just reflect status.
 */
export default function PushNotifications() {
  const status = useRequest(statusRequest, { manual: true });

  // Re-check when the user returns to the tab (e.g. after toggling the OS
  // permission), so the status reflects without a manual refresh.
  useEffect(() => {
    status.run();
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        status.run();
      }
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const data = status.data;
  const enabled = !!data?.enabled;

  return (
    <div className="flex flex-col space-y-2">
      {enabled ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
            Enabled
          </span>
          <span className="text-zinc-500 dark:text-zinc-400">
            You&apos;ll get release alerts and updates on{' '}
            {(data?.platforms ?? []).join(', ') || 'this device'}
            {(data?.count ?? 0) > 1 ? ` (${data?.count} devices)` : ''}.
          </span>
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Not enabled. Install the RAS app and allow notifications to get release alerts and
            updates pushed to your phone. You&apos;ll still receive everything on raspicks.com.
          </p>
          <AppStoreBadges />
        </>
      )}
    </div>
  );
}
